import { getApp, getApps, initializeApp, type FirebaseApp } from "firebase/app";

export interface FirebaseClientSettings {
  appCheckSiteKey: string;
  appCheckDebug: boolean;
  useAuthEmulator: boolean;
  authEmulatorUrl: string;
  useFirestoreEmulator: boolean;
  firestoreEmulatorHost: string;
  firestoreEmulatorPort: number;
  useFunctionsEmulator: boolean;
  functionsEmulatorHost: string;
  functionsEmulatorPort: number;
}

export interface FirebaseClient {
  app: FirebaseApp;
  settings: FirebaseClientSettings;
}

const APP_NAME = "zsombzilla-web";
let singletonClient: FirebaseClient | null | undefined;
let appCheckInitialization: Promise<void> | undefined;

function readFirebaseClient(): FirebaseClient | null {
  const useAuthEmulator = import.meta.env.VITE_FIREBASE_USE_AUTH_EMULATOR === "true";
  const useFunctionsEmulator =
    import.meta.env.VITE_FIREBASE_USE_FUNCTIONS_EMULATOR === "true";
  const useFirestoreEmulator =
    import.meta.env.VITE_FIREBASE_USE_FIRESTORE_EMULATOR === "true";
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY || (useAuthEmulator ? "demo-api-key" : "");
  const authDomain =
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ||
    (useAuthEmulator ? "demo-zsombzilla-dev.firebaseapp.com" : "");
  const projectId =
    import.meta.env.VITE_FIREBASE_PROJECT_ID || (useAuthEmulator ? "demo-zsombzilla-dev" : "");
  const appId =
    import.meta.env.VITE_FIREBASE_APP_ID || (useAuthEmulator ? "demo-zsombzilla-dev" : "");

  if (!apiKey || !authDomain || !projectId || !appId) {
    return null;
  }

  const app = getApps().some((candidate) => candidate.name === APP_NAME)
    ? getApp(APP_NAME)
    : initializeApp({ apiKey, authDomain, projectId, appId }, APP_NAME);

  return {
    app,
    settings: {
      appCheckSiteKey: import.meta.env.VITE_FIREBASE_APP_CHECK_SITE_KEY || "",
      appCheckDebug:
        import.meta.env.DEV && import.meta.env.VITE_FIREBASE_APP_CHECK_DEBUG === "true",
      useAuthEmulator,
      authEmulatorUrl:
        import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_URL || "http://127.0.0.1:9099",
      useFirestoreEmulator,
      firestoreEmulatorHost:
        import.meta.env.VITE_FIREBASE_FIRESTORE_EMULATOR_HOST || "127.0.0.1",
      firestoreEmulatorPort: Number(
        import.meta.env.VITE_FIREBASE_FIRESTORE_EMULATOR_PORT || "8080",
      ),
      useFunctionsEmulator,
      functionsEmulatorHost:
        import.meta.env.VITE_FIREBASE_FUNCTIONS_EMULATOR_HOST || "127.0.0.1",
      functionsEmulatorPort: Number(
        import.meta.env.VITE_FIREBASE_FUNCTIONS_EMULATOR_PORT || "5001",
      ),
    },
  };
}

export function getFirebaseClient(): FirebaseClient | null {
  if (singletonClient === undefined) {
    singletonClient = readFirebaseClient();
  }
  return singletonClient;
}

export function initializeFirebaseAppCheck(): Promise<void> {
  if (appCheckInitialization) {
    return appCheckInitialization;
  }

  appCheckInitialization = (async () => {
    const client = getFirebaseClient();
    if (
      !client ||
      !client.settings.appCheckSiteKey ||
      client.settings.useAuthEmulator ||
      client.settings.useFirestoreEmulator ||
      client.settings.useFunctionsEmulator
    ) {
      return;
    }

    if (client.settings.appCheckDebug) {
      const debugGlobal = globalThis as typeof globalThis & {
        FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean;
      };
      debugGlobal.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }

    const { initializeAppCheck, ReCaptchaEnterpriseProvider } = await import(
      "firebase/app-check"
    );
    initializeAppCheck(client.app, {
      provider: new ReCaptchaEnterpriseProvider(client.settings.appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  })();

  return appCheckInitialization;
}
